import { useEffect, useState } from "react";
import { Icon } from "./icons";
import { useContent } from "./content";
import {
  useCounter,
  useInView,
  usePrefersReducedMotion,
} from "./hooks";
import { ProvenanceMock, EquipmentMock } from "./Mockups";
import { FestivalSurface } from "./FestivalSurface";
import { APP_URL, STEP_ICONS, FEATURE_ICONS, TALLY, TIER_PRICES } from "./data";

function CueHead({ sig, title, dim, lead }: { sig: string; title: string; dim?: string; lead?: string }) {
  return (
    <>
      <div className="cue__head" data-reveal>
        <span className="cue__sig">SIG.{sig}</span>
        <h2 className="cue__title">{title} {dim && <span className="dim">{dim}</span>}</h2>
        <span className="cue__rule" />
      </div>
      {lead && <p className="cue__lead" data-reveal>{lead}</p>}
    </>
  );
}
const Points = ({ items }: { items: string[] }) => (
  <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
    {items.map((l) => <div key={l} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13.5, color: "var(--text-2)" }}><Icon name="check" size={15} style={{ color: "var(--accent)" }} />{l}</div>)}
  </div>
);

export function Nav() {
  const [sc, setSc] = useState(false);
  const t = useContent().nav;
  useEffect(() => { const on = () => setSc(window.scrollY > 12); on(); window.addEventListener("scroll", on, { passive: true }); return () => window.removeEventListener("scroll", on); }, []);
  return (
    <nav className="v2-nav" data-scrolled={sc ? "1" : "0"}>
      <div className="v2-wrap v2-nav__in">
        <a className="v2-brand" href="#cue-input"><img className="v2-brand__logo" src="/trace-logo.svg" alt="TRACE" width={92} height={20} /><span className="v2-brand__sig">{t.tagline}</span></a>
        <div className="v2-nav__links">
          <a className="v2-nav__link v2-nav__link--hide" href="#cue-read">01 · {t.read}</a>
          <a className="v2-nav__link v2-nav__link--hide" href="#cue-arrange">03 · {t.arrange}</a>
          <a className="v2-nav__link v2-nav__link--hide" href="#cue-plans">09 · {t.plans}</a>
          <a className="v2-nav__link" href={APP_URL + "/login"}>{t.login}</a>
          <a className="btn btn--accent" href={APP_URL + "/register"}>{t.getStarted}</a>
        </div>
      </div>
    </nav>
  );
}

export function SignalChain() {
  const t = useContent().chain;
  return (
    <section className="cue" id="cue-read" data-cue="READ"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} lead={t.lead} />
      <div className="chain" data-reveal>
        <div className="chain__flow" /><div className="chain__dot" />
        {t.steps.map((st, i) => (
          <div key={i} className="chain__cell">
            <div className="chain__ix">SIG.01.{i + 1}</div>
            <div className="chain__ico"><Icon name={STEP_ICONS[i] ?? "file"} size={19} /></div>
            <div className="chain__name">{st.title}</div>
            <div className="chain__desc">{st.desc}</div>
          </div>
        ))}
      </div>
    </div></section>
  );
}

export function Resolve() {
  const t = useContent().resolve;
  return (
    <section className="cue" id="cue-resolve" data-cue="RESOLVE"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} />
      <div className="guard" style={{ gridTemplateColumns: "0.9fr 1.1fr" }}>
        <div data-reveal><p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--text-2)", margin: 0 }}>{t.body}</p><Points items={t.points} /></div>
        <div data-reveal><ProvenanceMock /></div>
      </div>
    </div></section>
  );
}

export function ScheduleSection() {
  const t = useContent().arrange;
  return (
    <section className="cue" id="cue-arrange" data-cue="ARRANGE"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} lead={t.lead} />
      <div data-reveal><FestivalSurface /></div>
    </div></section>
  );
}

export function Guard() {
  const t = useContent().guard;
  const [ref, inv] = useInView<HTMLDivElement>(0.4);
  const reduce = usePrefersReducedMotion();
  const [resolved, setResolved] = useState(false);
  useEffect(() => {
    if (reduce) {
      setResolved(true);
      return;
    }
    if (inv) {
      const to = window.setTimeout(() => setResolved(true), 1500);
      return () => clearTimeout(to);
    }
  }, [inv, reduce]);
  return (
    <section className="cue" id="cue-guard" data-cue="GUARD"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} />
      <div className="guard">
        <div data-reveal><p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--text-2)", margin: 0 }}>{t.body}</p><Points items={t.points} /></div>
        <div className="guard__panel" ref={ref} data-reveal>
          <span className="guard__pill"><Icon name="triangle" size={13} />{resolved ? "1 critical · resolving" : "2 critical · 4 total"}</span>
          <div className={"guard__card" + (resolved ? " resolved" : "")}>
            <div className="guard__ctop"><span className="guard__sev">{resolved ? "resolved" : "critical"}</span><span className="guard__where">Main · Fri 21:00</span></div>
            <div className="guard__ctitle">Monitor exclusion — TALE OF US</div>
            <div className="guard__cdesc">Rider forbids d&b on Main, but Main PA is d&b J-Series. {resolved ? "Reassigned to Stone stage." : "Confirm alternative or reassign."}</div>
            <div className="guard__fix"><div className="guard__fixrow"><Icon name="check" size={14} />Moved to Stone · email drafted to production</div></div>
          </div>
        </div>
      </div>
    </div></section>
  );
}

function TallyBox({ to, unit, l, fill }: { to: number; unit: string; l: string; fill: number }) {
  const [ref, inv] = useInView<HTMLDivElement>(0.5);
  const reduce = usePrefersReducedMotion();
  const n = useCounter(to, inv);
  return (
    <div className="tallybox" ref={ref}>
      <div className="tallybox__n">{n}{unit && <span className="u">{unit}</span>}</div>
      <div className="tallybox__l">{l}</div>
      <div className="tallybox__bar"><span className="tallybox__fill" style={{ width: (inv || reduce) ? fill + "%" : "0%" }} /></div>
    </div>
  );
}
export function Tally() {
  const t = useContent().tally;
  return (
    <section className="cue" id="cue-tally" data-cue="TALLY"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} lead={t.lead} />
      <div className="guard" style={{ gridTemplateColumns: "1.05fr .95fr", alignItems: "stretch" }}>
        <div className="tally" data-reveal>{t.boxes.map((b, i) => { const d = TALLY[i] ?? { to: 0, unit: "", fill: 0 }; return <TallyBox key={i} to={d.to} unit={d.unit} l={b.l} fill={d.fill} />; })}</div>
        <div data-reveal><EquipmentMock /></div>
      </div>
    </div></section>
  );
}

const CAP_STAT = ["PDF → JSON", "per-stage", "auto-derived", "severity-ranked", "tier-based", "1-click", "Gantt"];
export function Capabilities() {
  const t = useContent().capabilities;
  return (
    <section className="cue" id="cue-caps" data-cue="CAPS"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} />
      <div className="caps" data-reveal>
        {t.items.map((f, i) => (
          <div key={i} className="cap">
            <span className="cap__ix">06.{i + 1}</span>
            <span className="cap__name"><span className="cap__ico"><Icon name={FEATURE_ICONS[i] ?? "file"} size={17} /></span>{f.title}</span>
            <span className="cap__desc">{f.desc}</span>
            <span className="cap__stat"><span className="chip">{CAP_STAT[i]}</span></span>
          </div>
        ))}
      </div>
    </div></section>
  );
}

export function Statement() {
  const lines = useContent().statement;
  return (
    <section className="stmt" data-reveal><div className="v2-wrap">
      {lines.map((l, i) => <p key={i} className="stmt__line">{i === lines.length - 1 ? <>Trust every <span className="stmt__hot">number</span>.</> : l}</p>)}
    </div></section>
  );
}

export function SocialProof() {
  const t = useContent().proof;
  return (
    <section className="cue" id="cue-proof" data-cue="PROOF"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} />
      <div className="proof__grid">
        {t.testimonials.map((tm, i) => (
          <figure key={i} className="quote" data-reveal style={{ transitionDelay: i * 80 + "ms" }}>
            <blockquote className="quote__q">{tm.quote}</blockquote>
            <figcaption className="quote__by"><span className="quote__name">{tm.name}</span><span className="quote__role">{tm.role}</span></figcaption>
          </figure>
        ))}
      </div>
    </div></section>
  );
}

export function Pricing() {
  const t = useContent().pricing;
  const [annual, setAnnual] = useState(false);
  return (
    <section className="cue" id="cue-plans" data-cue="PLANS"><div className="v2-wrap">
      <div className="lp-center" data-reveal>
        <CueHead sig={t.sig} title={t.title} dim={t.dim} lead={t.lead} />
        <div className="v2price__toggle">
          <button data-on={annual ? "0" : "1"} onClick={() => setAnnual(false)}>{t.toggle.monthly.toUpperCase()}</button>
          <button data-on={annual ? "1" : "0"} onClick={() => setAnnual(true)}>{t.toggle.annual.toUpperCase()} <span className="v2price__save">{t.toggle.save}</span></button>
        </div>
      </div>
      <div className="v2tiers" data-reveal>
        {t.tiers.map((tier, i) => {
          const price = TIER_PRICES[i] ?? { monthly: null, annual: null }; const custom = price.monthly === null;
          const shown = custom ? t.toggle.custom : annual && price.annual ? Math.round(price.annual / 12) : price.monthly;
          const savings = price.monthly && price.annual ? Math.round((1 - price.annual / (price.monthly * 12)) * 100) : null;
          const href = price.ctaHref || (APP_URL + "/register");
          return (
            <div key={i} className={"v2tier" + (price.hot ? " v2tier--hot" : "")}>
              {price.hot && <div className="v2tier__pop">◆ {t.toggle.mostPopular}</div>}
              <div className="v2tier__name">{tier.name}</div>
              <div className="v2tier__price">{custom ? <span className="amt">{t.toggle.custom}</span> : <><span className="amt">€{shown}</span><span className="per">{t.toggle.perMonth}</span></>}</div>
              <div className="v2tier__annual">{annual && !custom && price.annual ? `€${price.annual}/yr · −${savings}%` : ""}</div>
              <div className="v2tier__desc">{tier.desc}</div>
              <div className="v2tier__cta"><a className={"btn " + (price.hot ? "btn--accent" : "btn--ghost")} href={href}>{tier.cta}</a></div>
              <div className="v2tier__feats">{tier.feats.map((f) => <div key={f} className="v2tier__feat"><Icon name="check" size={14} />{f}</div>)}</div>
            </div>
          );
        })}
      </div>
    </div></section>
  );
}

export function FAQ() {
  const t = useContent().faq;
  return (
    <section className="cue" id="cue-faq" data-cue="FAQ"><div className="v2-wrap">
      <CueHead sig={t.sig} title={t.title} dim={t.dim} />
      <div className="faq" data-reveal>
        {t.items.map((f, i) => (
          <details key={i} className="faq__item" open={i === 0}>
            <summary className="faq__q"><span>{f.q}</span><span className="faq__mk" /></summary>
            <div className="faq__a">{f.a}</div>
          </details>
        ))}
      </div>
    </div></section>
  );
}

export function Output() {
  const t = useContent().output;
  const f = useContent().footer;
  // Prerendered at build time: the server markup and the first client render
  // must match, so start from the build year (compile-time constant, same in
  // both bundles — see build-defines.ts) and reconcile to the visitor's clock
  // after hydration (a runtime `new Date()` here mismatches every New Year
  // until the next deploy).
  const [year, setYear] = useState<number>(__BUILD_YEAR__);
  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);
  return (
    <>
      <section className="cue v2out" id="cue-output" data-cue="OUTPUT"><div className="v2-wrap" data-reveal>
        <div className="v2out__sig">SIG.11 — SIGNAL OUT</div>
        <h2 className="v2out__title">{t.title}</h2>
        <p className="v2out__sub">{t.body}</p>
        <div className="v2out__cta">
          <a className="btn btn--accent btn--lg" href={APP_URL + "/register"}>{t.ctaPrimary}<Icon name="arrowRight" size={16} className="btn__arrow" /></a>
          <a className="btn btn--ghost btn--lg" href="#cue-plans">{t.ctaSecondary}</a>
        </div>
      </div></section>
      <footer className="v2foot"><div className="v2-wrap v2foot__in">
        <a className="v2-brand" href="#cue-input"><img className="v2-brand__logo" src="/trace-logo.svg" alt="TRACE" width={92} height={20} style={{ opacity: .7 }} /><span className="v2-brand__sig">by Formray</span></a>
        <div className="v2foot__links"><a href="#cue-read">{f.links.read}</a><a href="#cue-plans">{f.links.plans}</a><a href="/technical-rider">Technical rider</a><a href="/glossary">Glossary</a><a href="mailto:info@formray.io">{f.links.contact}</a></div>
        <div className="v2foot__cr">{f.copyright.replace("{year}", String(year))}</div>
      </div></footer>
    </>
  );
}
